import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { clubhouseApi } from '../utils/clubhouseApi';
import { Eye, EyeOff } from 'lucide-react';
import './ClubhouseAuth.css';



export const ClubhouseLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isAccountDeleted, setIsAccountDeleted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
    setIsAccountDeleted(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setIsAccountDeleted(false);

    try {
      const response = await clubhouseApi.post(`/api/clubhouse/auth/login`, formData);

      // Auth is now via httpOnly cookie (set server-side). Only cache the
      // non-sensitive user object for instant UI rendering on next page load.
      localStorage.setItem('clubhouse_user', JSON.stringify(response.data.user));

      // Redirect to dashboard
      navigate('/clubhouse/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Login failed. Please try again.';
      const isDeleted = err.response?.headers?.['x-account-deleted'] === 'true' || 
                        detail.includes('deleted');
      
      setIsAccountDeleted(isDeleted);
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="clubhouse-auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <h1>Welcome to The Clubhouse</h1>
            <p>Sign in to access your member dashboard</p>
          </div>

          {error && (
            <div className={`error-message ${isAccountDeleted ? 'deleted-account' : ''}`}>
              {error}
              {isAccountDeleted && (
                <Link to="/member/recover" className="recover-link">
                  Recover my account →
                </Link>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="you@example.com"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="form-input"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <div className="forgot-password-link">
                <Link to="/clubhouse/forgot-password">Forgot password?</Link>
              </div>
            </div>

            <button 
              type="submit" 
              className="btn-primary btn-large"
              disabled={loading}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="auth-footer">
            <p>
              Don&apos;t have an account? <Link to="/clubhouse/register">Join The Clubhouse</Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
